import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Checkbox,
  FormGroup
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import UpcomingMaintenanceItemSummary, {
  useResolvedUpcomingItem,
  hasUpcomingSource,
  hasAnySource,
  IntimationStoredSourceSummary
} from './UpcomingMaintenanceItemSummary';

const StandaloneIntimationResponseDialog = ({ open, onClose, intimation, onSuccess, upcomingMaintenance = [], displayTimezone }) => {
  const [action, setAction] = useState('ACKNOWLEDGE');
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const [createWorkOrder, setCreateWorkOrder] = useState(true);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { showToast } = useToast();
  const { item: sourceUpcomingItem, loading: loadingSourceItem } = useResolvedUpcomingItem(
    intimation,
    upcomingMaintenance,
    open
  );

  useEffect(() => {
    if (intimation) {
      setAction('ACKNOWLEDGE');
      setWorkOrderNumber('');
      setCreateWorkOrder(true);
      setRemarks('');
      setError('');
    }
  }, [intimation]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (action === 'ACKNOWLEDGE') {
        if (createWorkOrder === false && !workOrderNumber.trim()) {
          setError('Enter work order number or check "Create work order from this intimation"');
          return;
        }
      }
      if (action === 'REQUEST_CLARIFICATION' && !remarks.trim()) {
        setError('Remarks are required when requesting clarification');
        return;
      }

      const payload = {
        action,
        remarks: remarks.trim() || undefined
      };
      if (action === 'ACKNOWLEDGE') {
        payload.createWorkOrder = createWorkOrder === true;
        if (!createWorkOrder && workOrderNumber.trim()) {
          payload.workOrderNumber = workOrderNumber.trim();
        }
      }

      await axios.post(`/api/intimations/${intimation._id}/respond`, payload);

      showToast(
        action === 'ACKNOWLEDGE'
          ? (createWorkOrder ? 'Work order created and intimation acknowledged.' : 'Intimation acknowledged.')
          : 'Clarification request sent.',
        'success'
      );
      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to respond to intimation';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!intimation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Respond to Intimation</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Title: <strong>{intimation.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{intimation.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        {hasUpcomingSource(intimation) && (
          loadingSourceItem ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Loading upcoming maintenance details…
            </Typography>
          ) : sourceUpcomingItem ? (
            <UpcomingMaintenanceItemSummary
              item={sourceUpcomingItem}
              displayTimezone={displayTimezone}
              title="Linked upcoming maintenance item"
            />
          ) : (
            <IntimationStoredSourceSummary intimation={intimation} />
          )
        )}
        {!hasUpcomingSource(intimation) && hasAnySource(intimation) && (
          <IntimationStoredSourceSummary intimation={intimation} />
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Intimation details</Typography>
          {intimation.includedItems && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Included:</Typography>
              <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>{intimation.includedItems}</Typography>
            </Box>
          )}
          {intimation.excludedItems && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Excluded:</Typography>
              <Typography variant="body2" sx={{ ml: 2 }}>{intimation.excludedItems}</Typography>
            </Box>
          )}
          {intimation.intimationDescription && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Description:</Typography>
              <Typography variant="body2" sx={{ ml: 2 }}>{intimation.intimationDescription}</Typography>
            </Box>
          )}
        </Alert>

        {intimation.planningClarificationReply && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Planning&apos;s reply (after your clarification request):</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{intimation.planningClarificationReply}</Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Response</FormLabel>
          <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
            <FormControlLabel value="ACKNOWLEDGE" control={<Radio />} label="Acknowledge (create/link work order)" />
            <FormControlLabel value="REQUEST_CLARIFICATION" control={<Radio />} label="Request clarification" />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {action === 'ACKNOWLEDGE' && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createWorkOrder}
                      onChange={(e) => setCreateWorkOrder(e.target.checked)}
                    />
                  }
                  label="Create work order from this intimation (recommended)"
                />
              </FormGroup>
            </Grid>
            {!createWorkOrder && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Work order number"
                  value={workOrderNumber}
                  onChange={(e) => setWorkOrderNumber(e.target.value)}
                  placeholder="Enter the work order number you created"
                  helperText="If you created the work order elsewhere, enter its number here"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Remarks (optional)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>
          </Grid>
        )}

        {action === 'REQUEST_CLARIFICATION' && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Clarification remarks *"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="What information do you need from Planning?"
                required
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            (action === 'ACKNOWLEDGE' && !createWorkOrder && !workOrderNumber.trim()) ||
            (action === 'REQUEST_CLARIFICATION' && !remarks.trim())
          }
        >
          {loading ? 'Submitting...' : action === 'ACKNOWLEDGE' ? 'Acknowledge' : 'Request clarification'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StandaloneIntimationResponseDialog;
